
import React, { useState, useEffect, useMemo } from 'react';
import { collection, serverTimestamp, doc, getDoc, writeBatch } from "@firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { CryptoLogos } from '../constants';
import { AdminWallets, SystemSettings } from '../types';

type Currency = string;
type Network = 'ERC20' | 'TRC20' | 'BEP20';

interface DepositModalProps {
  onClose: () => void;
}

const DepositModal: React.FC<DepositModalProps> = ({ onClose }) => {
  const [step, setStep] = useState<'currency' | 'network' | 'amount' | 'address' | 'final_pending'>('currency');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<Network | null>(null);
  const [depositAmount, setDepositAmount] = useState<string>('');
  const [txHash, setTxHash] = useState<string>('');
  const [adminWallets, setAdminWallets] = useState<AdminWallets | null>(null);
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [walletSnap, settingsSnap] = await Promise.all([
          getDoc(doc(db, 'system', 'wallets')),
          getDoc(doc(db, 'system', 'settings'))
        ]);
        
        if (walletSnap.exists()) setAdminWallets(walletSnap.data() as AdminWallets);
        else setAdminWallets({});

        if (settingsSnap.exists()) setSettings(settingsSnap.data() as SystemSettings);
      } catch (err) {
        console.error("Config fetch error:", err);
      }
    };
    fetchConfig();
  }, []);

  const currencies = useMemo(() => {
    if (!adminWallets) return [];
    const keys = Object.keys(adminWallets);
    const baseCurrencies = new Set<string>();
    keys.forEach(key => {
      const base = key.split('_')[0];
      baseCurrencies.add(base);
    });

    const defaultCurrencies: Record<string, { name: string; icon: React.ReactNode; color: string }> = {
      'BTC': { name: 'Bitcoin', icon: <CryptoLogos.BTC />, color: '#F7931A' },
      'ETH': { name: 'Ethereum', icon: <CryptoLogos.ETH />, color: '#627EEA' },
      'USDT': { name: 'Tether', icon: <CryptoLogos.USDT />, color: '#26A17B' },
      'XRP': { name: 'Ripple', icon: <CryptoLogos.XRP />, color: '#23292F' },
      'BNB': { name: 'BNB', icon: <CryptoLogos.BNB />, color: '#F3BA2F' },
      'TRX': { name: 'TRON', icon: <CryptoLogos.TRX />, color: '#EF0027' },
      'TRON': { name: 'TRON', icon: <CryptoLogos.TRX />, color: '#EF0027' },
      'SOL': { name: 'Solana', icon: <CryptoLogos.SOL />, color: '#14F195' },
      'SOLANA': { name: 'Solana', icon: <CryptoLogos.SOL />, color: '#14F195' },
    };

    return Array.from(baseCurrencies).map(id => {
      if (defaultCurrencies[id]) {
        return { id, ...defaultCurrencies[id] };
      }
      return {
        id,
        name: id,
        icon: <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-[10px] font-black">{id.slice(0, 3)}</div>,
        color: '#AEB877'
      };
    });
  }, [adminWallets]);

  const networks: Network[] = ['ERC20', 'TRC20', 'BEP20'];

  const handleCurrencySelect = (curr: Currency) => {
    setSelectedCurrency(curr);
    // Only USDT displays networks, ETH now skips network selection
    if (curr === 'USDT') setStep('network');
    else setStep('amount');
  };

  const handleNetworkSelect = (net: Network) => {
    setSelectedNetwork(net);
    setStep('amount');
  };

  const handleAmountSubmit = () => {
    const val = parseFloat(depositAmount);
    const minDep = settings?.minDeposit || 100;

    if (isNaN(val) || val <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (val < minDep) {
      setError(`Minimum deposit amount is $${minDep.toLocaleString()}`);
      return;
    }

    setError('');
    setStep('address');
  };

  const getAddress = () => {
    if (!adminWallets) return 'Syncing transmission node...';
    if (!selectedCurrency) return 'Node Restricted';

    const complexKey = selectedNetwork ? `${selectedCurrency}_${selectedNetwork}` : selectedCurrency;
    const walletMap = adminWallets as Record<string, string>;
    
    if (walletMap[complexKey]) return walletMap[complexKey];
    if (walletMap[selectedCurrency]) return walletMap[selectedCurrency];
    
    return 'Transmission node offline. Contact support.';
  };

  const handleSentFunds = async () => {
    if (!auth.currentUser || !selectedCurrency) {
      setError('Neural handshake failed. Please re-login.');
      return;
    }
    
    if (!txHash.trim()) {
      setError('Transaction Hash is required for verification.');
      return;
    }
    
    setSubmitting(true);
    setError('');

    try {
      const depositId = `DEP-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
      const depositData = {
        id: depositId,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        userName: auth.currentUser.displayName || 'Trader',
        amount: parseFloat(depositAmount),
        currency: selectedCurrency,
        network: selectedNetwork || 'DEFAULT',
        status: 'pending',
        txHash: txHash.trim(),
        createdAt: serverTimestamp()
      };
      
      const batch = writeBatch(db);
      
      // Document refs
      const globalDepRef = doc(db, 'deposits', depositId);
      const userDepRef = doc(db, 'users', auth.currentUser.uid, 'deposits', depositId);
      
      // Use set instead of update to ensure document creation, 
      // rules are now updated to allow update/set for these paths.
      batch.set(globalDepRef, depositData);
      batch.set(userDepRef, depositData);
      
      await batch.commit();
      setStep('final_pending');
    } catch (err: any) { 
      console.error("Critical Deposit Error:", err);
      setError("Terminal failed to broadcast transaction. Check system logs.");
    } finally { 
      setSubmitting(false); 
    }
  };

  const handleCopy = () => {
    const addr = getAddress();
    if (addr.includes('...') || addr.includes('Contact support')) return;
    navigator.clipboard.writeText(addr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isAddressInvalid = getAddress().includes('...') || getAddress().includes('Contact support');
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(getAddress())}&bgcolor=000000&color=AEB877&margin=10`;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl animate-in fade-in duration-300">
      <div className="w-full max-w-md bg-[#0A0A0A] border border-[#1A1A1A] rounded-[24px] p-6 sm:p-8 relative shadow-2xl overflow-y-auto max-h-[90vh] no-scrollbar">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#444] hover:text-white transition-colors p-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
        
        {step !== 'final_pending' && (
          <div className="mb-6">
            <h2 className="text-xl font-black uppercase text-white mb-1 tracking-tighter">Deposit</h2>
            <p className="text-[7px] font-black text-[#444] uppercase tracking-[0.4em]">Asset Acquisition Protocol</p>
          </div>
        )}

        {step === 'currency' && (
          <div className="grid grid-cols-2 gap-3">
            {currencies.map(curr => (
              <button key={curr.id} onClick={() => handleCurrencySelect(curr.id)} className="flex flex-col items-center justify-center p-6 bg-[#050505] border border-[#1A1A1A] rounded-[16px] hover:border-[#AEB877] transition-all group shadow-inner">
                <div className="w-8 h-8 mb-3 group-hover:scale-110 transition-transform">{curr.icon}</div>
                <span className="text-[10px] font-black uppercase text-white tracking-widest">{curr.id}</span>
              </button>
            ))}
          </div>
        )}

        {step === 'network' && (
          <div className="space-y-3">
             <p className="text-[8px] font-black text-[#666] uppercase tracking-[0.3em] mb-4 text-center">Select Blockchain Network</p>
             {networks.map(net => (
              <button key={net} onClick={() => handleNetworkSelect(net)} className="w-full p-4 bg-[#050505] border border-[#1A1A1A] rounded-xl text-left hover:border-[#AEB877] transition-all flex justify-between items-center group">
                <span className="font-black text-white text-xs uppercase tracking-widest">{net}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-white/10 group-hover:text-white transition-all"><path d="m9 18 6-6-6-6"/></svg>
              </button>
            ))}
          </div>
        )}

        {step === 'amount' && (
          <div className="space-y-6">
            <div>
               <label className="block text-[8px] font-black text-[#444] uppercase tracking-widest mb-2 ml-1">USD Volume (Min: ${settings?.minDeposit || 100})</label>
               <div className="relative">
                 <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#AEB877] font-black text-lg">$</span>
                 <input 
                   type="number" 
                   value={depositAmount} 
                   autoFocus 
                   className="w-full bg-black border border-white/5 rounded-xl py-4 pl-10 pr-4 text-white font-black text-2xl focus:border-[#AEB877] outline-none shadow-inner" 
                   placeholder="0.00" 
                   onChange={e => setDepositAmount(e.target.value)} 
                 />
               </div>
            </div>
            {error && <p className="text-red-500 text-center uppercase text-[8px] font-black bg-red-500/10 p-3 rounded-lg border border-red-500/20">{error}</p>}
            <button onClick={handleAmountSubmit} className="w-full bg-[#AEB877] text-black py-4 rounded-xl font-black uppercase text-xs tracking-widest shadow-xl hover:bg-white transition-all">Confirm Allocation</button>
          </div>
        )}

        {step === 'address' && (
          <div className="text-center space-y-4 animate-in zoom-in-95">
            <div className="w-full space-y-3">
               {!isAddressInvalid && (
                 <div className="mx-auto w-40 h-40 bg-black border border-[#AEB877]/20 rounded-2xl p-2 shadow-[0_0_30px_rgba(174,184,119,0.1)] relative group overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#AEB877]/5 to-transparent pointer-events-none"></div>
                    <img 
                      src={qrCodeUrl} 
                      alt="Wallet QR" 
                      className="w-full h-full object-contain rounded-lg opacity-90 group-hover:opacity-100 transition-opacity" 
                    />
                 </div>
               )}

               <div className="flex items-center gap-3 bg-black p-4 rounded-2xl border border-white/5">
                  <div className="text-left flex-1">
                     <p className="text-[8px] font-black text-[#444] uppercase tracking-widest mb-0.5">Total to send</p>
                     <p className="text-xl font-black text-white uppercase tabular-nums">${depositAmount} <span className="text-[10px] text-[#666]">{selectedCurrency}</span></p>
                  </div>
               </div>
               
               <div className="relative group">
                 <label className="block text-[8px] font-black text-[#444] uppercase tracking-widest mb-2 text-left ml-1">Transmission Address</label>
                 <div className={`w-full bg-black border border-[#1A1A1A] rounded-xl py-4 px-4 text-[9px] font-mono break-all text-left ${isAddressInvalid ? 'text-red-500/50' : 'text-[#888]'}`}>
                   {getAddress()}
                 </div>
                 {!isAddressInvalid && (
                  <button onClick={handleCopy} className="absolute right-3 bottom-3 text-[#AEB877] hover:scale-110 transition-transform bg-[#0A0A0A] p-1.5 rounded-lg border border-white/5 shadow-lg">
                    {copied ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>}
                  </button>
                 )}
               </div>

               <div className="space-y-2">
                  <label className="block text-[8px] font-black text-[#444] uppercase tracking-widest text-left ml-1">Transaction Hash / Proof</label>
                  <input 
                    type="text" 
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    placeholder="Enter blockchain tx hash..."
                    className="w-full bg-black border border-white/10 rounded-xl py-4 px-4 text-[10px] font-mono focus:border-[#AEB877] outline-none transition-all"
                  />
               </div>
            </div>

            <div className="p-3 bg-[#AEB877]/5 border border-[#AEB877]/10 rounded-xl">
               <p className="text-[7px] text-[#AEB877] font-black uppercase tracking-[0.2em] leading-relaxed">
                 Scan QR or copy address to complete transmission. Paste the Tx Hash above to facilitate rapid audit.
               </p>
            </div>

            {error && <p className="text-red-500 text-center uppercase text-[8px] font-black">{error}</p>}
            
            <div className="space-y-2 pt-1">
              <button 
                onClick={handleSentFunds} 
                disabled={submitting || isAddressInvalid} 
                className="w-full bg-[#AEB877] text-black py-4 rounded-xl font-black uppercase text-[10px] tracking-[0.2em] shadow-2xl disabled:opacity-50 transition-all hover:bg-white active:scale-95"
              >
                  {submitting ? 'Transmitting...' : 'I have sent funds'}
              </button>
              <button onClick={() => setStep('amount')} className="w-full text-[8px] font-black text-[#444] uppercase tracking-widest hover:text-white py-1 transition-colors">Modify Parameters</button>
            </div>
          </div>
        )}

        {step === 'final_pending' && (
          <div className="text-center py-6 animate-in zoom-in-95">
             <div className="w-16 h-16 bg-[#F6A500]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#F6A500]/20 animate-pulse">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F6A500" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
             </div>
             <h3 className="text-xl font-black uppercase text-white tracking-tighter mb-2">Transmission Logged</h3>
             <p className="text-[9px] text-[#666] max-w-xs mx-auto font-bold uppercase tracking-widest leading-relaxed">
               Your deposit with hash <span className="text-white font-mono">{txHash.slice(0, 12)}...</span> is currently being verified by the neural core. Funds will be credited once confirmed.
             </p>
             <button onClick={onClose} className="mt-8 w-full bg-white text-black py-4 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-[#AEB877] transition-all">Close Terminal</button>
          </div>
        )}
      </div>
    </div>
  );
};

const InputGroup = ({ label, value, onChange, type = "text" }: any) => (
  <div className="space-y-2"><label className="text-[9px] font-black text-[#444] uppercase ml-1 tracking-widest">{label}</label><input type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full bg-black border border-white/5 rounded-2xl py-5 px-6 text-white text-xl font-black outline-none focus:border-[#AEB877] transition-all" placeholder="Transmit..." /></div>
);

export default DepositModal;
